/**
 * Custom GraphQL query, mutation, and subscription strings for the DataStore-to-GraphQL migration.
 *
 * These are hand-written (not auto-generated) to include all fields needed by repositories
 * and to provide lightweight version-only queries for optimistic concurrency.
 */

// ---------------------------------------------------------------------------
// Version-only queries (lightweight, for fetchVersion pattern)
// ---------------------------------------------------------------------------

export const getConsultDtoVersion = /* GraphQL */ `
    query GetConsultDtoVersion($id: ID!) {
        getConsultDto(id: $id) {
            id
            _version
        }
    }
`

export const getAudioDtoVersion = /* GraphQL */ `
    query GetAudioDtoVersion($id: ID!) {
        getAudioDto(id: $id) {
            id
            _version
        }
    }
`

export const getConsultEntityDtoVersion = /* GraphQL */ `
    query GetConsultEntityDtoVersion($id: ID!) {
        getConsultEntityDto(id: $id) {
            id
            _version
        }
    }
`

export const getConsultTranscriptionDtoVersion = /* GraphQL */ `
    query GetConsultTranscriptionDtoVersion($id: ID!) {
        getConsultTranscriptionDto(id: $id) {
            id
            _version
        }
    }
`

export const getConsultDiarizationDtoVersion = /* GraphQL */ `
    query GetConsultDiarizationDtoVersion($id: ID!) {
        getConsultDiarizationDto(id: $id) {
            id
            _version
        }
    }
`

export const getConsultTemplateDtoVersion = /* GraphQL */ `
    query GetConsultTemplateDtoVersion($id: ID!) {
        getConsultTemplateDto(id: $id) {
            id
            _version
        }
    }
`

export const getConsultPatientDtoVersion = /* GraphQL */ `
    query GetConsultPatientDtoVersion($id: ID!) {
        getConsultPatientDto(id: $id) {
            id
            _version
        }
    }
`

export const getFormDtoVersion = /* GraphQL */ `
    query GetFormDtoVersion($id: ID!) {
        getFormDto(id: $id) {
            id
            _version
        }
    }
`

// ---------------------------------------------------------------------------
// ConsultDto mutations
// ---------------------------------------------------------------------------

export const createConsultDtoMutation = /* GraphQL */ `
    mutation CreateConsultDto($input: CreateConsultDtoInput!) {
        createConsultDto(input: $input) {
            id
            owner
            authId
            patientId
            title
            type
            catalogueTemplateId
            catalogueTemplateVersion
            startDate
            completionDate
            rating
            summary
            state
            tags
            accuracy
            stateTimestamps {
                state
                timestamp
            }
            audioId
            entities {
                entityConfidence
                entityEnd
                entity_type
                entityStart
                entityText
            }
            template {
                id
                title
                type
                content {
                    title
                    detail
                    order
                }
                webContent
                model
                owner
            }
            templateDelta {
                id
                title
                type
                content {
                    title
                    detail
                    order
                }
                webContent
                model
                owner
            }
            additionalTemplates {
                id
                title
                type
                content {
                    title
                    detail
                    order
                }
                webContent
                model
                owner
            }
            additionalTemplatesDelta {
                type
                templates {
                    id
                    title
                    type
                    webContent
                    model
                    owner
                }
            }
            attachments {
                attachmentId
                attachmentType
                content
                contentStorageKey
                thumbnail
                thumbnailStorageKey
            }
            matchedPatients {
                id
                gender
            }
            confirmedPatient {
                id
                givenNames
                familyName
                dateOfBirth
                sexAtBirth
                Address
            }
            createdAt
            updatedAt
            _version
            _deleted
            _lastChangedAt
        }
    }
`

export const updateConsultDtoMutation = /* GraphQL */ `
    mutation UpdateConsultDto($input: UpdateConsultDtoInput!) {
        updateConsultDto(input: $input) {
            id
            owner
            authId
            patientId
            title
            type
            catalogueTemplateId
            catalogueTemplateVersion
            startDate
            completionDate
            rating
            summary
            state
            tags
            accuracy
            stateTimestamps {
                state
                timestamp
            }
            audioId
            entities {
                entityConfidence
                entityEnd
                entity_type
                entityStart
                entityText
            }
            template {
                id
                title
                type
                content {
                    title
                    detail
                    order
                }
                webContent
                model
                owner
            }
            attachments {
                attachmentId
                attachmentType
                content
                contentStorageKey
                thumbnail
                thumbnailStorageKey
            }
            matchedPatients {
                id
                gender
            }
            confirmedPatient {
                id
                givenNames
                familyName
                dateOfBirth
                sexAtBirth
                Address
            }
            createdAt
            updatedAt
            _version
            _deleted
            _lastChangedAt
        }
    }
`

export const deleteConsultDtoMutation = /* GraphQL */ `
    mutation DeleteConsultDto($input: DeleteConsultDtoInput!) {
        deleteConsultDto(input: $input) {
            id
            _version
            _deleted
            _lastChangedAt
        }
    }
`

// ---------------------------------------------------------------------------
// AudioDto mutations
// ---------------------------------------------------------------------------

export const createAudioDtoMutation = /* GraphQL */ `
    mutation CreateAudioDto($input: CreateAudioDtoInput!) {
        createAudioDto(input: $input) {
            id
            url
            storageKey
            duration
            consultId
            createdAt
            updatedAt
            _version
            _deleted
            _lastChangedAt
        }
    }
`

export const updateAudioDtoMutation = /* GraphQL */ `
    mutation UpdateAudioDto($input: UpdateAudioDtoInput!) {
        updateAudioDto(input: $input) {
            id
            url
            storageKey
            duration
            consultId
            createdAt
            updatedAt
            _version
            _deleted
            _lastChangedAt
        }
    }
`

export const deleteAudioDtoMutation = /* GraphQL */ `
    mutation DeleteAudioDto($input: DeleteAudioDtoInput!) {
        deleteAudioDto(input: $input) {
            id
            _version
            _deleted
            _lastChangedAt
        }
    }
`

// ---------------------------------------------------------------------------
// ConsultEntityDto mutations
// ---------------------------------------------------------------------------

export const createConsultEntityDtoMutation = /* GraphQL */ `
    mutation CreateConsultEntityDto($input: CreateConsultEntityDtoInput!) {
        createConsultEntityDto(input: $input) {
            id
            consultId
            owner
            entities {
                entityConfidence
                entityEnd
                entity_type
                entityStart
                entityText
            }
            createdAt
            updatedAt
            _version
            _deleted
            _lastChangedAt
        }
    }
`

export const updateConsultEntityDtoMutation = /* GraphQL */ `
    mutation UpdateConsultEntityDto($input: UpdateConsultEntityDtoInput!) {
        updateConsultEntityDto(input: $input) {
            id
            consultId
            owner
            entities {
                entityConfidence
                entityEnd
                entity_type
                entityStart
                entityText
            }
            createdAt
            updatedAt
            _version
            _deleted
            _lastChangedAt
        }
    }
`

export const deleteConsultEntityDtoMutation = /* GraphQL */ `
    mutation DeleteConsultEntityDto($input: DeleteConsultEntityDtoInput!) {
        deleteConsultEntityDto(input: $input) {
            id
            _version
            _deleted
            _lastChangedAt
        }
    }
`

// ---------------------------------------------------------------------------
// ConsultTranscriptionDto mutations
// ---------------------------------------------------------------------------

export const createConsultTranscriptionDtoMutation = /* GraphQL */ `
    mutation CreateConsultTranscriptionDto(
        $input: CreateConsultTranscriptionDtoInput!
    ) {
        createConsultTranscriptionDto(input: $input) {
            id
            consultId
            owner
            clientTranscription
            cloudTranscription
            createdAt
            updatedAt
            _version
            _deleted
            _lastChangedAt
        }
    }
`

export const updateConsultTranscriptionDtoMutation = /* GraphQL */ `
    mutation UpdateConsultTranscriptionDto(
        $input: UpdateConsultTranscriptionDtoInput!
    ) {
        updateConsultTranscriptionDto(input: $input) {
            id
            consultId
            owner
            clientTranscription
            cloudTranscription
            createdAt
            updatedAt
            _version
            _deleted
            _lastChangedAt
        }
    }
`

export const deleteConsultTranscriptionDtoMutation = /* GraphQL */ `
    mutation DeleteConsultTranscriptionDto(
        $input: DeleteConsultTranscriptionDtoInput!
    ) {
        deleteConsultTranscriptionDto(input: $input) {
            id
            _version
            _deleted
            _lastChangedAt
        }
    }
`

// ---------------------------------------------------------------------------
// ConsultDiarizationDto mutations
// ---------------------------------------------------------------------------

export const createConsultDiarizationDtoMutation = /* GraphQL */ `
    mutation CreateConsultDiarizationDto(
        $input: CreateConsultDiarizationDtoInput!
    ) {
        createConsultDiarizationDto(input: $input) {
            id
            consultId
            owner
            diarization {
                confidence
                end
                speaker
                start
                text
            }
            createdAt
            updatedAt
            _version
            _deleted
            _lastChangedAt
        }
    }
`

export const updateConsultDiarizationDtoMutation = /* GraphQL */ `
    mutation UpdateConsultDiarizationDto(
        $input: UpdateConsultDiarizationDtoInput!
    ) {
        updateConsultDiarizationDto(input: $input) {
            id
            consultId
            owner
            diarization {
                confidence
                end
                speaker
                start
                text
            }
            createdAt
            updatedAt
            _version
            _deleted
            _lastChangedAt
        }
    }
`

export const deleteConsultDiarizationDtoMutation = /* GraphQL */ `
    mutation DeleteConsultDiarizationDto(
        $input: DeleteConsultDiarizationDtoInput!
    ) {
        deleteConsultDiarizationDto(input: $input) {
            id
            _version
            _deleted
            _lastChangedAt
        }
    }
`

// ---------------------------------------------------------------------------
// ConsultTemplateDto mutations
// ---------------------------------------------------------------------------

export const createConsultTemplateDtoMutation = /* GraphQL */ `
    mutation CreateConsultTemplateDto($input: CreateConsultTemplateDtoInput!) {
        createConsultTemplateDto(input: $input) {
            id
            consultId
            owner
            template {
                id
                title
                type
                content {
                    title
                    detail
                    order
                }
                webContent
                model
                owner
            }
            templateDelta {
                id
                title
                type
                content {
                    title
                    detail
                    order
                }
                webContent
                model
                owner
            }
            createdAt
            updatedAt
            _version
            _deleted
            _lastChangedAt
        }
    }
`

export const updateConsultTemplateDtoMutation = /* GraphQL */ `
    mutation UpdateConsultTemplateDto($input: UpdateConsultTemplateDtoInput!) {
        updateConsultTemplateDto(input: $input) {
            id
            consultId
            owner
            template {
                id
                title
                type
                content {
                    title
                    detail
                    order
                }
                webContent
                model
                owner
            }
            templateDelta {
                id
                title
                type
                content {
                    title
                    detail
                    order
                }
                webContent
                model
                owner
            }
            createdAt
            updatedAt
            _version
            _deleted
            _lastChangedAt
        }
    }
`

export const deleteConsultTemplateDtoMutation = /* GraphQL */ `
    mutation DeleteConsultTemplateDto($input: DeleteConsultTemplateDtoInput!) {
        deleteConsultTemplateDto(input: $input) {
            id
            _version
            _deleted
            _lastChangedAt
        }
    }
`

// ---------------------------------------------------------------------------
// ConsultPatientDto mutations
// ---------------------------------------------------------------------------

export const createConsultPatientDtoMutation = /* GraphQL */ `
    mutation CreateConsultPatientDto($input: CreateConsultPatientDtoInput!) {
        createConsultPatientDto(input: $input) {
            id
            consultId
            owner
            patientId
            attachedPatient {
                id
                identifiers {
                    use
                    system
                    value
                    assigner
                }
                names {
                    name
                    preferred
                    source
                    use
                    givenNames
                    familyName
                }
                dateOfBirth {
                    value
                    source
                }
                dateOfDeath {
                    value
                    source
                }
                address {
                    value
                    source
                    type
                    use
                }
                gender
                ethnicities {
                    value
                    code
                }
                birthplace {
                    country
                    placeOfBirth
                    source
                }
                citizenship {
                    source
                    status
                }
            }
            matchedPatients {
                id
                identifiers {
                    use
                    system
                    value
                    assigner
                }
                names {
                    name
                    preferred
                    source
                    use
                    givenNames
                    familyName
                }
                dateOfBirth {
                    value
                    source
                }
                dateOfDeath {
                    value
                    source
                }
                address {
                    value
                    source
                    type
                    use
                }
                gender
                ethnicities {
                    value
                    code
                }
                birthplace {
                    country
                    placeOfBirth
                    source
                }
                citizenship {
                    source
                    status
                }
            }
            createdAt
            updatedAt
            _version
            _deleted
            _lastChangedAt
        }
    }
`

export const updateConsultPatientDtoMutation = /* GraphQL */ `
    mutation UpdateConsultPatientDto($input: UpdateConsultPatientDtoInput!) {
        updateConsultPatientDto(input: $input) {
            id
            consultId
            owner
            patientId
            attachedPatient {
                id
                identifiers {
                    use
                    system
                    value
                    assigner
                }
                names {
                    name
                    preferred
                    source
                    use
                    givenNames
                    familyName
                }
                dateOfBirth {
                    value
                    source
                }
                dateOfDeath {
                    value
                    source
                }
                address {
                    value
                    source
                    type
                    use
                }
                gender
                ethnicities {
                    value
                    code
                }
                birthplace {
                    country
                    placeOfBirth
                    source
                }
                citizenship {
                    source
                    status
                }
            }
            matchedPatients {
                id
                identifiers {
                    use
                    system
                    value
                    assigner
                }
                names {
                    name
                    preferred
                    source
                    use
                    givenNames
                    familyName
                }
                dateOfBirth {
                    value
                    source
                }
                dateOfDeath {
                    value
                    source
                }
                address {
                    value
                    source
                    type
                    use
                }
                gender
                ethnicities {
                    value
                    code
                }
                birthplace {
                    country
                    placeOfBirth
                    source
                }
                citizenship {
                    source
                    status
                }
            }
            createdAt
            updatedAt
            _version
            _deleted
            _lastChangedAt
        }
    }
`

export const deleteConsultPatientDtoMutation = /* GraphQL */ `
    mutation DeleteConsultPatientDto($input: DeleteConsultPatientDtoInput!) {
        deleteConsultPatientDto(input: $input) {
            id
            _version
            _deleted
            _lastChangedAt
        }
    }
`

// ---------------------------------------------------------------------------
// FormDto mutations
// ---------------------------------------------------------------------------

export const createFormDtoMutation = /* GraphQL */ `
    mutation CreateFormDto($input: CreateFormDtoInput!) {
        createFormDto(input: $input) {
            id
            type
            consultId
            fhirResponseId
            title
            status
            patientId
            patientName
            sentDate
            sections {
                title
                rows {
                    columns {
                        linkId
                        label
                        content
                        code
                        system
                        codings {
                            system
                            code
                            display
                        }
                    }
                }
            }
            response
            createdAt
            updatedAt
            _version
            _deleted
            _lastChangedAt
        }
    }
`

export const updateFormDtoMutation = /* GraphQL */ `
    mutation UpdateFormDto($input: UpdateFormDtoInput!) {
        updateFormDto(input: $input) {
            id
            type
            consultId
            fhirResponseId
            title
            status
            patientId
            patientName
            sentDate
            sections {
                title
                rows {
                    columns {
                        linkId
                        label
                        content
                        code
                        system
                        codings {
                            system
                            code
                            display
                        }
                    }
                }
            }
            response
            createdAt
            updatedAt
            _version
            _deleted
            _lastChangedAt
        }
    }
`

export const deleteFormDtoMutation = /* GraphQL */ `
    mutation DeleteFormDto($input: DeleteFormDtoInput!) {
        deleteFormDto(input: $input) {
            id
            _version
            _deleted
            _lastChangedAt
        }
    }
`

// ---------------------------------------------------------------------------
// GSI queries (query by consultId via secondary index)
// ---------------------------------------------------------------------------

export const consultEntityDtosByConsultId = /* GraphQL */ `
    query ConsultEntityDtosByConsultId(
        $consultId: ID!
        $sortDirection: ModelSortDirection
        $filter: ModelConsultEntityDtoFilterInput
        $limit: Int
        $nextToken: String
    ) {
        consultEntityDtosByConsultId(
            consultId: $consultId
            sortDirection: $sortDirection
            filter: $filter
            limit: $limit
            nextToken: $nextToken
        ) {
            items {
                id
                consultId
                owner
                entities {
                    entityConfidence
                    entityEnd
                    entity_type
                    entityStart
                    entityText
                }
                createdAt
                updatedAt
                _version
                _deleted
                _lastChangedAt
            }
            nextToken
            startedAt
        }
    }
`

export const consultTranscriptionDtosByConsultId = /* GraphQL */ `
    query ConsultTranscriptionDtosByConsultId(
        $consultId: ID!
        $sortDirection: ModelSortDirection
        $filter: ModelConsultTranscriptionDtoFilterInput
        $limit: Int
        $nextToken: String
    ) {
        consultTranscriptionDtosByConsultId(
            consultId: $consultId
            sortDirection: $sortDirection
            filter: $filter
            limit: $limit
            nextToken: $nextToken
        ) {
            items {
                id
                consultId
                owner
                clientTranscription
                cloudTranscription
                createdAt
                updatedAt
                _version
                _deleted
                _lastChangedAt
            }
            nextToken
            startedAt
        }
    }
`

export const consultDiarizationDtosByConsultId = /* GraphQL */ `
    query ConsultDiarizationDtosByConsultId(
        $consultId: ID!
        $sortDirection: ModelSortDirection
        $filter: ModelConsultDiarizationDtoFilterInput
        $limit: Int
        $nextToken: String
    ) {
        consultDiarizationDtosByConsultId(
            consultId: $consultId
            sortDirection: $sortDirection
            filter: $filter
            limit: $limit
            nextToken: $nextToken
        ) {
            items {
                id
                consultId
                owner
                diarization {
                    confidence
                    end
                    speaker
                    start
                    text
                }
                createdAt
                updatedAt
                _version
                _deleted
                _lastChangedAt
            }
            nextToken
            startedAt
        }
    }
`

export const consultTemplateDtosByConsultId = /* GraphQL */ `
    query ConsultTemplateDtosByConsultId(
        $consultId: ID!
        $sortDirection: ModelSortDirection
        $filter: ModelConsultTemplateDtoFilterInput
        $limit: Int
        $nextToken: String
    ) {
        consultTemplateDtosByConsultId(
            consultId: $consultId
            sortDirection: $sortDirection
            filter: $filter
            limit: $limit
            nextToken: $nextToken
        ) {
            items {
                id
                consultId
                owner
                template {
                    id
                    title
                    type
                    content {
                        title
                        detail
                        order
                    }
                    webContent
                    model
                    owner
                }
                templateDelta {
                    id
                    title
                    type
                    content {
                        title
                        detail
                        order
                    }
                    webContent
                    model
                    owner
                }
                createdAt
                updatedAt
                _version
                _deleted
                _lastChangedAt
            }
            nextToken
            startedAt
        }
    }
`

export const consultPatientDtosByConsultId = /* GraphQL */ `
    query ConsultPatientDtosByConsultId(
        $consultId: ID!
        $sortDirection: ModelSortDirection
        $filter: ModelConsultPatientDtoFilterInput
        $limit: Int
        $nextToken: String
    ) {
        consultPatientDtosByConsultId(
            consultId: $consultId
            sortDirection: $sortDirection
            filter: $filter
            limit: $limit
            nextToken: $nextToken
        ) {
            items {
                id
                consultId
                owner
                patientId
                attachedPatient {
                    id
                    identifiers {
                        use
                        system
                        value
                        assigner
                    }
                    names {
                        name
                        preferred
                        source
                        use
                        givenNames
                        familyName
                    }
                    dateOfBirth {
                        value
                        source
                    }
                    dateOfDeath {
                        value
                        source
                    }
                    address {
                        value
                        source
                        type
                        use
                    }
                    gender
                    ethnicities {
                        value
                        code
                    }
                    birthplace {
                        country
                        placeOfBirth
                        source
                    }
                    citizenship {
                        source
                        status
                    }
                }
                matchedPatients {
                    id
                    identifiers {
                        use
                        system
                        value
                        assigner
                    }
                    names {
                        name
                        preferred
                        source
                        use
                        givenNames
                        familyName
                    }
                    dateOfBirth {
                        value
                        source
                    }
                    dateOfDeath {
                        value
                        source
                    }
                    address {
                        value
                        source
                        type
                        use
                    }
                    gender
                    ethnicities {
                        value
                        code
                    }
                    birthplace {
                        country
                        placeOfBirth
                        source
                    }
                    citizenship {
                        source
                        status
                    }
                }
                createdAt
                updatedAt
                _version
                _deleted
                _lastChangedAt
            }
            nextToken
            startedAt
        }
    }
`

// ---------------------------------------------------------------------------
// List queries
// ---------------------------------------------------------------------------

export const listConsultPatientDtosQuery = /* GraphQL */ `
    query ListConsultPatientDtos(
        $filter: ModelConsultPatientDtoFilterInput
        $limit: Int
        $nextToken: String
    ) {
        listConsultPatientDtos(
            filter: $filter
            limit: $limit
            nextToken: $nextToken
        ) {
            items {
                id
                consultId
                owner
                patientId
                createdAt
                updatedAt
                _version
                _deleted
                _lastChangedAt
            }
            nextToken
            startedAt
        }
    }
`

export const listAssistantDtosQuery = /* GraphQL */ `
    query ListAssistantDtos(
        $filter: ModelAssistantDtoFilterInput
        $limit: Int
        $nextToken: String
    ) {
        listAssistantDtos(
            filter: $filter
            limit: $limit
            nextToken: $nextToken
        ) {
            items {
                id
                assistantId
                name
                instructions
                tools
                model
                modelName
                description
                isForm
                isSupplementary
                isTable
                type
                creator
                createdAt
                updatedAt
                _version
                _deleted
                _lastChangedAt
            }
            nextToken
            startedAt
        }
    }
`

export const listFormDtosQuery = /* GraphQL */ `
    query ListFormDtos(
        $filter: ModelFormDtoFilterInput
        $limit: Int
        $nextToken: String
    ) {
        listFormDtos(filter: $filter, limit: $limit, nextToken: $nextToken) {
            items {
                id
                type
                consultId
                fhirResponseId
                title
                status
                patientId
                patientName
                sentDate
                sections {
                    title
                    rows {
                        columns {
                            linkId
                            label
                            content
                            code
                            system
                            codings {
                                system
                                code
                                display
                            }
                        }
                    }
                }
                response
                createdAt
                updatedAt
                _version
                _deleted
                _lastChangedAt
            }
            nextToken
            startedAt
        }
    }
`

export const listQuestionnaireDtosQuery = /* GraphQL */ `
    query ListQuestionnaireDtos(
        $filter: ModelQuestionnaireDtoFilterInput
        $limit: Int
        $nextToken: String
    ) {
        listQuestionnaireDtos(
            filter: $filter
            limit: $limit
            nextToken: $nextToken
        ) {
            items {
                id
                sourceUrl
                name
                json
                createdAt
                updatedAt
                _version
                _deleted
                _lastChangedAt
            }
            nextToken
            startedAt
        }
    }
`

export const listConsultAdditionalTemplateDtosQuery = /* GraphQL */ `
    query ListConsultAdditionalTemplateDtos(
        $filter: ModelConsultAdditionalTemplateDtoFilterInput
        $limit: Int
        $nextToken: String
    ) {
        listConsultAdditionalTemplateDtos(
            filter: $filter
            limit: $limit
            nextToken: $nextToken
        ) {
            items {
                id
                consultId
                owner
                additionalTemplates {
                    id
                    title
                    type
                    content {
                        title
                        detail
                        order
                    }
                    webContent
                    model
                    owner
                }
                additionalTemplatesDelta {
                    type
                    templates {
                        id
                        title
                        type
                        webContent
                        model
                        owner
                    }
                }
                createdAt
                updatedAt
                _version
                _deleted
                _lastChangedAt
            }
            nextToken
            startedAt
        }
    }
`

// ---------------------------------------------------------------------------
// Get queries (single record by ID)
// ---------------------------------------------------------------------------

export const getFormDtoQuery = /* GraphQL */ `
    query GetFormDto($id: ID!) {
        getFormDto(id: $id) {
            id
            type
            consultId
            fhirResponseId
            title
            status
            patientId
            patientName
            sentDate
            sections {
                title
                rows {
                    columns {
                        linkId
                        label
                        content
                        code
                        system
                        codings {
                            system
                            code
                            display
                        }
                    }
                }
            }
            response
            createdAt
            updatedAt
            _version
            _deleted
            _lastChangedAt
        }
    }
`

export const getQuestionnaireDtoQuery = /* GraphQL */ `
    query GetQuestionnaireDto($id: ID!) {
        getQuestionnaireDto(id: $id) {
            id
            sourceUrl
            name
            json
            createdAt
            updatedAt
            _version
            _deleted
            _lastChangedAt
        }
    }
`

export const getConsultAdditionalTemplateDtoQuery = /* GraphQL */ `
    query GetConsultAdditionalTemplateDto($id: ID!) {
        getConsultAdditionalTemplateDto(id: $id) {
            id
            consultId
            owner
            additionalTemplates {
                id
                title
                type
                content {
                    title
                    detail
                    order
                }
                webContent
                model
                owner
            }
            additionalTemplatesDelta {
                type
                templates {
                    id
                    title
                    type
                    webContent
                    model
                    owner
                }
            }
            createdAt
            updatedAt
            _version
            _deleted
            _lastChangedAt
        }
    }
`

export const getSubscriptionDtoQuery = /* GraphQL */ `
    query GetSubscriptionDto($id: ID!) {
        getSubscriptionDto(id: $id) {
            id
            type
            monthlyConsultLimit
            consultsUsedThisMonth
            description
            price
            subsidy
            stripeId
            stripePriceId
            stripeProductId
            startDate
            endDate
            status
            lastUpgradeDate
            createdAt
            updatedAt
            _version
            _deleted
            _lastChangedAt
        }
    }
`

export const getTuhiUserDtoQuery = /* GraphQL */ `
    query GetTuhiUserDto($id: ID!) {
        getTuhiUserDto(id: $id) {
            id
            sub
            confidenceLevel
            firstName
            lastName
            email
            birthDate
            avatarUrl
            avatarStorageKey
            subscriptionId
            createdAt
            updatedAt
            _version
            _deleted
            _lastChangedAt
        }
    }
`

// ---------------------------------------------------------------------------
// Subscription queries for models NOT already in subscriptions.ts
// (subscriptions.ts already has: ConsultDto, AssistantDto, TuhiUserDto,
//  HandshakeDto, AuditLogDto, ErrorLogDto)
// ---------------------------------------------------------------------------

export const onCreateConsultTemplateDto = /* GraphQL */ `
    subscription OnCreateConsultTemplateDto(
        $owner: String!
        $filter: ModelSubscriptionConsultTemplateDtoFilterInput
    ) {
        onCreateConsultTemplateDto(owner: $owner, filter: $filter) {
            id
            consultId
            owner
            template {
                id
                title
                type
                content {
                    title
                    detail
                    order
                }
                webContent
                model
                owner
            }
            templateDelta {
                id
                title
                type
                content {
                    title
                    detail
                    order
                }
                webContent
                model
                owner
            }
            createdAt
            updatedAt
            _version
            _deleted
            _lastChangedAt
        }
    }
`

export const onUpdateConsultTemplateDto = /* GraphQL */ `
    subscription OnUpdateConsultTemplateDto(
        $owner: String!
        $filter: ModelSubscriptionConsultTemplateDtoFilterInput
    ) {
        onUpdateConsultTemplateDto(owner: $owner, filter: $filter) {
            id
            consultId
            owner
            template {
                id
                title
                type
                content {
                    title
                    detail
                    order
                }
                webContent
                model
                owner
            }
            templateDelta {
                id
                title
                type
                content {
                    title
                    detail
                    order
                }
                webContent
                model
                owner
            }
            createdAt
            updatedAt
            _version
            _deleted
            _lastChangedAt
        }
    }
`

export const onCreateConsultAdditionalTemplateDto = /* GraphQL */ `
    subscription OnCreateConsultAdditionalTemplateDto(
        $owner: String!
        $filter: ModelSubscriptionConsultAdditionalTemplateDtoFilterInput
    ) {
        onCreateConsultAdditionalTemplateDto(owner: $owner, filter: $filter) {
            id
            consultId
            owner
            additionalTemplates {
                id
                title
                type
                content {
                    title
                    detail
                    order
                }
                webContent
                model
                owner
            }
            additionalTemplatesDelta {
                type
                templates {
                    id
                    title
                    type
                    webContent
                    model
                    owner
                }
            }
            createdAt
            updatedAt
            _version
            _deleted
            _lastChangedAt
        }
    }
`

export const onUpdateConsultAdditionalTemplateDto = /* GraphQL */ `
    subscription OnUpdateConsultAdditionalTemplateDto(
        $owner: String!
        $filter: ModelSubscriptionConsultAdditionalTemplateDtoFilterInput
    ) {
        onUpdateConsultAdditionalTemplateDto(owner: $owner, filter: $filter) {
            id
            consultId
            owner
            additionalTemplates {
                id
                title
                type
                content {
                    title
                    detail
                    order
                }
                webContent
                model
                owner
            }
            additionalTemplatesDelta {
                type
                templates {
                    id
                    title
                    type
                    webContent
                    model
                    owner
                }
            }
            createdAt
            updatedAt
            _version
            _deleted
            _lastChangedAt
        }
    }
`

export const onCreateConsultPatientDto = /* GraphQL */ `
    subscription OnCreateConsultPatientDto(
        $owner: String!
        $filter: ModelSubscriptionConsultPatientDtoFilterInput
    ) {
        onCreateConsultPatientDto(owner: $owner, filter: $filter) {
            id
            consultId
            owner
            patientId
            attachedPatient {
                id
                gender
            }
            matchedPatients {
                id
                gender
            }
            createdAt
            updatedAt
            _version
            _deleted
            _lastChangedAt
        }
    }
`

export const onUpdateConsultPatientDto = /* GraphQL */ `
    subscription OnUpdateConsultPatientDto(
        $owner: String!
        $filter: ModelSubscriptionConsultPatientDtoFilterInput
    ) {
        onUpdateConsultPatientDto(owner: $owner, filter: $filter) {
            id
            consultId
            owner
            patientId
            attachedPatient {
                id
                gender
            }
            matchedPatients {
                id
                gender
            }
            createdAt
            updatedAt
            _version
            _deleted
            _lastChangedAt
        }
    }
`

export const onCreateFormDto = /* GraphQL */ `
    subscription OnCreateFormDto($filter: ModelSubscriptionFormDtoFilterInput) {
        onCreateFormDto(filter: $filter) {
            id
            type
            consultId
            fhirResponseId
            title
            status
            patientId
            patientName
            sentDate
            sections {
                title
                rows {
                    columns {
                        linkId
                        label
                        content
                        code
                        system
                        codings {
                            system
                            code
                            display
                        }
                    }
                }
            }
            response
            createdAt
            updatedAt
            _version
            _deleted
            _lastChangedAt
        }
    }
`

export const onUpdateFormDto = /* GraphQL */ `
    subscription OnUpdateFormDto($filter: ModelSubscriptionFormDtoFilterInput) {
        onUpdateFormDto(filter: $filter) {
            id
            type
            consultId
            fhirResponseId
            title
            status
            patientId
            patientName
            sentDate
            sections {
                title
                rows {
                    columns {
                        linkId
                        label
                        content
                        code
                        system
                        codings {
                            system
                            code
                            display
                        }
                    }
                }
            }
            response
            createdAt
            updatedAt
            _version
            _deleted
            _lastChangedAt
        }
    }
`

export const onUpdateSubscriptionDto = /* GraphQL */ `
    subscription OnUpdateSubscriptionDto(
        $filter: ModelSubscriptionSubscriptionDtoFilterInput
    ) {
        onUpdateSubscriptionDto(filter: $filter) {
            id
            type
            monthlyConsultLimit
            consultsUsedThisMonth
            description
            price
            subsidy
            stripeId
            stripePriceId
            stripeProductId
            startDate
            endDate
            status
            lastUpgradeDate
            createdAt
            updatedAt
            _version
            _deleted
            _lastChangedAt
        }
    }
`

// ---------------------------------------------------------------------------
// AuditLogDto mutations
// ---------------------------------------------------------------------------

export const createAuditLogDtoMutation = /* GraphQL */ `
    mutation CreateAuditLogDto($input: CreateAuditLogDtoInput!) {
        createAuditLogDto(input: $input) {
            id
            consultId
            cpn
            createdAt
            updatedAt
            _version
            _deleted
            _lastChangedAt
        }
    }
`

// ---------------------------------------------------------------------------
// Corrected ConsultDto queries (auto-generated queries.ts has stale fields)
// ---------------------------------------------------------------------------

export const getConsultDtoQuery = /* GraphQL */ `
    query GetConsultDto($id: ID!) {
        getConsultDto(id: $id) {
            id
            owner
            authId
            patientId
            title
            type
            catalogueTemplateId
            catalogueTemplateVersion
            startDate
            completionDate
            rating
            summary
            state
            tags
            accuracy
            stateTimestamps {
                state
                timestamp
            }
            audioId
            entities {
                entityConfidence
                entityEnd
                entity_type
                entityStart
                entityText
            }
            template {
                id
                title
                type
                content {
                    title
                    detail
                    order
                }
                webContent
                model
                owner
            }
            templateDelta {
                id
                title
                type
                content {
                    title
                    detail
                    order
                }
                webContent
                model
                owner
            }
            additionalTemplates {
                id
                title
                type
                content {
                    title
                    detail
                    order
                }
                webContent
                model
                owner
            }
            attachments {
                attachmentId
                attachmentType
                content
                contentStorageKey
                thumbnail
                thumbnailStorageKey
            }
            matchedPatients {
                id
                gender
            }
            confirmedPatient {
                id
                givenNames
                familyName
                dateOfBirth
                sexAtBirth
                Address
            }
            createdAt
            updatedAt
            _version
            _deleted
            _lastChangedAt
        }
    }
`

export const listConsultDtosQuery = /* GraphQL */ `
    query ListConsultDtos(
        $filter: ModelConsultDtoFilterInput
        $limit: Int
        $nextToken: String
    ) {
        listConsultDtos(filter: $filter, limit: $limit, nextToken: $nextToken) {
            items {
                id
                owner
                authId
                patientId
                title
                type
                startDate
                completionDate
                rating
                summary
                state
                tags
                accuracy
                audioId
                createdAt
                updatedAt
                _version
                _deleted
                _lastChangedAt
            }
            nextToken
            startedAt
        }
    }
`

/**
 * Date-sorted History query backed by the ConsultDto `byOwner` GSI
 * (@index sortKeyFields: ["startDate"]). Returns an owner's consults already
 * sorted by startDate — pass sortDirection: DESC for newest-first. This replaces
 * the listConsultDtos scan+filter, which returned items in arbitrary table order
 * and could omit the newest consults from the first page.
 */
export const consultDtosByOwnerAndStartDateQuery = /* GraphQL */ `
    query ConsultDtosByOwnerAndStartDate(
        $owner: String!
        $sortDirection: ModelSortDirection
        $filter: ModelConsultDtoFilterInput
        $limit: Int
        $nextToken: String
    ) {
        consultDtosByOwnerAndStartDate(
            owner: $owner
            sortDirection: $sortDirection
            filter: $filter
            limit: $limit
            nextToken: $nextToken
        ) {
            items {
                id
                owner
                authId
                patientId
                title
                type
                startDate
                completionDate
                rating
                summary
                state
                tags
                accuracy
                audioId
                attachments {
                    attachmentId
                    attachmentType
                    content
                    contentStorageKey
                    thumbnail
                    thumbnailStorageKey
                }
                createdAt
                updatedAt
                _version
                _deleted
                _lastChangedAt
            }
            nextToken
            startedAt
        }
    }
`

// ACA-185: lightweight per-user consult counter for the admin Users view.
// Selects only id + state (NO summary / clinical fields) so the admin list can
// count each user's consults without pulling PHI. Keyed on the byOwner GSI with
// a startDate sort-key condition so it returns only the requested period.
export const consultCountByOwnerSinceQuery = /* GraphQL */ `
    query ConsultCountByOwnerSince(
        $owner: String!
        $startDate: ModelStringKeyConditionInput
        $limit: Int
        $nextToken: String
    ) {
        consultDtosByOwnerAndStartDate(
            owner: $owner
            startDate: $startDate
            limit: $limit
            nextToken: $nextToken
        ) {
            items {
                id
                state
                _deleted
            }
            nextToken
        }
    }
`

// ---------------------------------------------------------------------------
// Corrected ConsultDto subscriptions (auto-generated subscriptions.ts has stale fields)
// ---------------------------------------------------------------------------

export const onCreateConsultDtoSubscription = /* GraphQL */ `
    subscription OnCreateConsultDto(
        $owner: String!
        $filter: ModelSubscriptionConsultDtoFilterInput
    ) {
        onCreateConsultDto(owner: $owner, filter: $filter) {
            id
            owner
            authId
            patientId
            title
            type
            catalogueTemplateId
            catalogueTemplateVersion
            startDate
            completionDate
            rating
            summary
            state
            tags
            accuracy
            audioId
            createdAt
            updatedAt
            _version
            _deleted
            _lastChangedAt
        }
    }
`

export const onUpdateConsultDtoSubscription = /* GraphQL */ `
    subscription OnUpdateConsultDto(
        $owner: String!
        $filter: ModelSubscriptionConsultDtoFilterInput
    ) {
        onUpdateConsultDto(owner: $owner, filter: $filter) {
            id
            owner
            authId
            patientId
            title
            type
            catalogueTemplateId
            catalogueTemplateVersion
            startDate
            completionDate
            rating
            summary
            state
            tags
            accuracy
            audioId
            createdAt
            updatedAt
            _version
            _deleted
            _lastChangedAt
        }
    }
`

// ---------------------------------------------------------------------------
// FormDto GSI query (by consultId)
// ---------------------------------------------------------------------------

export const formDtosByConsultIdQuery = /* GraphQL */ `
    query FormDtosByConsultIdAndSentDate(
        $consultId: ID!
        $sortDirection: ModelSortDirection
        $filter: ModelFormDtoFilterInput
        $limit: Int
        $nextToken: String
    ) {
        formDtosByConsultIdAndSentDate(
            consultId: $consultId
            sortDirection: $sortDirection
            filter: $filter
            limit: $limit
            nextToken: $nextToken
        ) {
            items {
                id
                type
                consultId
                fhirResponseId
                title
                status
                patientId
                patientName
                sentDate
                sections {
                    title
                    rows {
                        columns {
                            linkId
                            label
                            content
                            code
                            system
                            codings {
                                system
                                code
                                display
                            }
                        }
                    }
                }
                response
                createdAt
                updatedAt
                _version
                _deleted
                _lastChangedAt
            }
            nextToken
            startedAt
        }
    }
`

// ---------------------------------------------------------------------------
// FormDto subscriptions
// ---------------------------------------------------------------------------

export const onCreateFormDtoSubscription = /* GraphQL */ `
    subscription OnCreateFormDto($filter: ModelSubscriptionFormDtoFilterInput) {
        onCreateFormDto(filter: $filter) {
            id
            type
            consultId
            fhirResponseId
            title
            status
            patientId
            patientName
            sentDate
            sections {
                title
                rows {
                    columns {
                        linkId
                        label
                        content
                        code
                        system
                        codings {
                            system
                            code
                            display
                        }
                    }
                }
            }
            response
            createdAt
            updatedAt
            _version
            _deleted
            _lastChangedAt
        }
    }
`

export const onUpdateFormDtoSubscription = /* GraphQL */ `
    subscription OnUpdateFormDto($filter: ModelSubscriptionFormDtoFilterInput) {
        onUpdateFormDto(filter: $filter) {
            id
            type
            consultId
            fhirResponseId
            title
            status
            patientId
            patientName
            sentDate
            sections {
                title
                rows {
                    columns {
                        linkId
                        label
                        content
                        code
                        system
                        codings {
                            system
                            code
                            display
                        }
                    }
                }
            }
            response
            createdAt
            updatedAt
            _version
            _deleted
            _lastChangedAt
        }
    }
`

// ---------------------------------------------------------------------------
// ConsultEvidenceDto (ACA-37 Encounter Evidence). Owner read-only: every write
// goes through a backend endpoint, so there are no evidence mutations here.
// ---------------------------------------------------------------------------

export const consultEvidenceDtosByConsultQuery = /* GraphQL */ `
    query ConsultEvidenceDtosByConsult(
        $consultId: ID!
        $sortDirection: ModelSortDirection
        $limit: Int
        $nextToken: String
    ) {
        consultEvidenceDtosByConsult(
            consultId: $consultId
            sortDirection: $sortDirection
            limit: $limit
            nextToken: $nextToken
        ) {
            items {
                id
                consultId
                sourceType
                sourceName
                mimeType
                storageKey
                originSystem
                attachmentId
                status
                failureReason
                extractedText
                extractedTextKey
                included
                processedAt
                createdAt
                _deleted
            }
            nextToken
        }
    }
`

// The Lambdas write evidence rows over IAM, and a subscription only carries
// the fields the triggering mutation selected (the create selects id,
// consultId, status, included, createdAt). So these select only fields every
// evidence mutation returns, and the panel re-reads the list on each event.
export const onCreateConsultEvidenceDtoSubscription = /* GraphQL */ `
    subscription OnCreateConsultEvidenceDto(
        $filter: ModelSubscriptionConsultEvidenceDtoFilterInput
    ) {
        onCreateConsultEvidenceDto(filter: $filter) {
            id
            consultId
            status
            included
        }
    }
`

export const onUpdateConsultEvidenceDtoSubscription = /* GraphQL */ `
    subscription OnUpdateConsultEvidenceDto(
        $filter: ModelSubscriptionConsultEvidenceDtoFilterInput
    ) {
        onUpdateConsultEvidenceDto(filter: $filter) {
            id
            consultId
            status
            included
        }
    }
`

// ---------------------------------------------------------------------------
// ACA-37 regenerate (Req 4.1, 5.3). Small reads for the Regenerate button:
// the Current version number, the evidence it was built from, and whether the
// current documents carry manual edits. Writes go through the backend.
// ---------------------------------------------------------------------------

export const getConsultTemplateVersionNumberQuery = /* GraphQL */ `
    query GetConsultTemplateVersionNumber($id: ID!) {
        getConsultDto(id: $id) {
            id
            state
            templateVersion
            _deleted
        }
    }
`

// ACA-37 Req 1.7: the consult's attachments, read fresh for the Encounter
// Evidence panel. The details page often renders from the History list cache,
// which may not carry attachments, so the panel cannot rely on the cached row.
export const getConsultAttachmentsQuery = /* GraphQL */ `
    query GetConsultAttachments($id: ID!) {
        getConsultDto(id: $id) {
            id
            attachments {
                attachmentId
                attachmentType
                content
                contentStorageKey
                thumbnail
                thumbnailStorageKey
            }
            _deleted
        }
    }
`

export const getConsultTemplateVersionEvidenceQuery = /* GraphQL */ `
    query GetConsultTemplateVersionEvidence($id: ID!) {
        getConsultTemplateVersionDto(id: $id) {
            id
            version
            evidenceIds
            reason
            createdAt
            _deleted
        }
    }
`

export const consultTemplateDeltaByConsultIdQuery = /* GraphQL */ `
    query ConsultTemplateDeltaByConsultId($consultId: ID!) {
        consultTemplateDtosByConsultId(consultId: $consultId) {
            items {
                id
                templateDelta {
                    id
                }
                _deleted
            }
        }
    }
`

export const getConsultAdditionalTemplateDeltaQuery = /* GraphQL */ `
    query GetConsultAdditionalTemplateDelta($id: ID!) {
        getConsultAdditionalTemplateDto(id: $id) {
            id
            additionalTemplatesDelta {
                type
                templates {
                    id
                }
            }
            _deleted
        }
    }
`

// ---------------------------------------------------------------------------
// ACA-37 version history (Req 5.5, 5.6). The list reads summary fields only;
// a version's content is read when the clinician opens it. Version records
// are owner read-only; restore goes through the backend.
// ---------------------------------------------------------------------------

export const consultTemplateVersionHistoryQuery = /* GraphQL */ `
    query ConsultTemplateVersionHistory(
        $consultId: ID!
        $sortDirection: ModelSortDirection
        $limit: Int
        $nextToken: String
    ) {
        consultTemplateVersionDtosByConsult(
            consultId: $consultId
            sortDirection: $sortDirection
            limit: $limit
            nextToken: $nextToken
        ) {
            items {
                id
                version
                reason
                restoredFromVersion
                evidenceIds
                createdAt
                _deleted
            }
            nextToken
        }
    }
`

export const getConsultTemplateVersionContentQuery = /* GraphQL */ `
    query GetConsultTemplateVersionContent($id: ID!) {
        getConsultTemplateVersionDto(id: $id) {
            id
            version
            template {
                id
                title
                type
                content {
                    title
                    detail
                    order
                }
                webContent
            }
            additionalTemplates {
                id
                title
                type
                content {
                    title
                    detail
                    order
                }
                webContent
            }
            _deleted
        }
    }
`

// ---------------------------------------------------------------------------
// ACA-176 stage 1: mark ready for finalisation and recall. Kept as their own
// operations, not added to the shared consult selections: if a web build
// reaches an environment before its amplify push, only the finalisation panel
// fails, and the rest of the consult page keeps working. The mutation selects
// everything updateConsultDtoMutation does, because AppSync gives
// onUpdateConsultDto subscribers only the fields the mutation selected.
// ---------------------------------------------------------------------------

export const getConsultFinalisationQuery = /* GraphQL */ `
    query GetConsultFinalisation($id: ID!) {
        getConsultDto(id: $id) {
            id
            finalisationStatus
            finalisationNote
            finalisationRequestedAt
            finalisationQuery
            finalisationReviewerName
            _version
            _deleted
        }
    }
`

export const updateConsultFinalisationMutation = /* GraphQL */ `
    mutation UpdateConsultFinalisation($input: UpdateConsultDtoInput!) {
        updateConsultDto(input: $input) {
            id
            owner
            authId
            patientId
            title
            type
            catalogueTemplateId
            catalogueTemplateVersion
            startDate
            completionDate
            rating
            summary
            state
            tags
            accuracy
            stateTimestamps {
                state
                timestamp
            }
            audioId
            entities {
                entityConfidence
                entityEnd
                entity_type
                entityStart
                entityText
            }
            template {
                id
                title
                type
                content {
                    title
                    detail
                    order
                }
                webContent
                model
                owner
            }
            attachments {
                attachmentId
                attachmentType
                content
                contentStorageKey
                thumbnail
                thumbnailStorageKey
            }
            matchedPatients {
                id
                gender
            }
            confirmedPatient {
                id
                givenNames
                familyName
                dateOfBirth
                sexAtBirth
                Address
            }
            finalisationStatus
            finalisationNote
            finalisationRequestedAt
            finalisationQuery
            finalisationReviewerName
            createdAt
            updatedAt
            _version
            _deleted
            _lastChangedAt
        }
    }
`

// ---------------------------------------------------------------------------
// ACA-39: archive a consult (web). Own operations, same reason as ACA-176's
// above: a schema without ConsultDto.archived breaks only the archive control
// and the History "Archived" filter, not the consult page or the list. The
// mutation selects everything updateConsultFinalisationMutation does, plus
// archived, because subscribers only get the fields the mutation selected.
// ---------------------------------------------------------------------------

export const getConsultArchivedQuery = /* GraphQL */ `
    query GetConsultArchived($id: ID!) {
        getConsultDto(id: $id) {
            id
            archived
            _version
            _deleted
        }
    }
`

/** Ids of the owner's archived consults, for hiding them in History. */
export const archivedConsultIdsByOwnerQuery = /* GraphQL */ `
    query ArchivedConsultIdsByOwner(
        $owner: String!
        $limit: Int
        $nextToken: String
    ) {
        consultDtosByOwnerAndStartDate(
            owner: $owner
            filter: { archived: { eq: true } }
            limit: $limit
            nextToken: $nextToken
        ) {
            items {
                id
                _deleted
            }
            nextToken
        }
    }
`

export const updateConsultArchivedMutation = /* GraphQL */ `
    mutation UpdateConsultArchived($input: UpdateConsultDtoInput!) {
        updateConsultDto(input: $input) {
            id
            owner
            authId
            patientId
            title
            type
            catalogueTemplateId
            catalogueTemplateVersion
            startDate
            completionDate
            rating
            summary
            state
            tags
            accuracy
            stateTimestamps {
                state
                timestamp
            }
            audioId
            entities {
                entityConfidence
                entityEnd
                entity_type
                entityStart
                entityText
            }
            template {
                id
                title
                type
                content {
                    title
                    detail
                    order
                }
                webContent
                model
                owner
            }
            attachments {
                attachmentId
                attachmentType
                content
                contentStorageKey
                thumbnail
                thumbnailStorageKey
            }
            matchedPatients {
                id
                gender
            }
            confirmedPatient {
                id
                givenNames
                familyName
                dateOfBirth
                sexAtBirth
                Address
            }
            finalisationStatus
            finalisationNote
            finalisationRequestedAt
            finalisationQuery
            finalisationReviewerName
            archived
            createdAt
            updatedAt
            _version
            _deleted
            _lastChangedAt
        }
    }
`

// ---------------------------------------------------------------------------
// ACA-246: the user's viewed-output versions for a consult
// (ConsultViewedOutputsDto, one row per user per consult, id
// `${consultId}#${sub}`). Read and written only by the consult page's tab
// highlight.
// ---------------------------------------------------------------------------

export const getConsultViewedOutputsQuery = /* GraphQL */ `
    query GetConsultViewedOutputs($id: ID!) {
        getConsultViewedOutputsDto(id: $id) {
            id
            viewed
            _version
            _deleted
        }
    }
`

export const createConsultViewedOutputsMutation = /* GraphQL */ `
    mutation CreateConsultViewedOutputs(
        $input: CreateConsultViewedOutputsDtoInput!
    ) {
        createConsultViewedOutputsDto(input: $input) {
            id
            viewed
            _version
            _deleted
        }
    }
`

export const updateConsultViewedOutputsMutation = /* GraphQL */ `
    mutation UpdateConsultViewedOutputs(
        $input: UpdateConsultViewedOutputsDtoInput!
    ) {
        updateConsultViewedOutputsDto(input: $input) {
            id
            viewed
            _version
            _deleted
        }
    }
`
